import { IconLinkExternal16 } from '@warp-ds/icons/react';
import { Button, Expandable } from '@warp-ds/react';
import { useState } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import { QasaLandingPublishButton } from '@/components/button/QasaLandingPublishButton';
import ExpandableQuestion from '@/components/ExpandableQuestion';
import { QasaInfoBox } from '@/components/section/QasaInfoBox';
import { QasaSection } from '@/components/section/QasaSection';
import { useUnleash } from '@/hooks/unleash';
import { trackCreateOrRepublishAdButton, trackGoToExpiredAds } from '@/utils/tracking';

const AboutQasaPage = () => {
    const { baseUrl } = useConfig();
    const [expanded, setExpanded] = useState(false);
    const unleash = useUnleash();

    return (
        <div className="md:p-0 mx-0 md:mx-16">
            <h1 className="sr-only">Qasa i samarbeid med FINN</h1>
            <section
                className="mx-auto mt-16 px-[16] md:pl-[177] md:pr-[110] pb-32 md:pb-0 flex flex-col md:flex-row-reverse gap-8 md:gap-[80] lg:gap-[150] rounded-0 md:rounded-[32]"
                style={{ maxWidth: '1392px', backgroundColor: '#FFEBF2' }}>
                <p className="w-full mb-8 flex-1 mt-20 md:mt-40">
                    <img
                        src="https://images.finncdn.no/dynamic/1280w/lotr/qasa/ps-visual-qasa-finn.png"
                        alt="Eksempler på annonser"
                        className="w-full object-contain"
                    />
                </p>

                <div className="flex-1 pb-8 md:mt-40">
                    <p className="text-display mb-16 p-8 md:p-0 text-center md:text-left">
                        Som utleier er du alltid trygg når du leier ut med Qasa
                    </p>
                    <p className="text-body md:p-0 mb-24 text-center md:text-left">
                        <strong>Med Qasa får du en annonse som vises både på Qasa og FINN</strong>, i tillegg til annonsen tilbyr Qasa en
                        rekke tjenester som bistår og hjelper deg som utleier til å få et tryggere og mer forutsigbart utleieforhold.
                    </p>
                    <QasaLandingPublishButton />
                    <p className="mt-24 mb-8 font-bold md:p-0 t4 text-center md:text-left">
                        Har du en annonse du ønsker å republisere med Qasa?
                    </p>
                    <div className="flex text-center md:text-left flex-col md:flex-row gap-8">
                        <p>Alle bilder og boligdetaljer vil bli overført automatisk.</p>
                        <a
                            href={`${baseUrl}/my-items?statusFacetId=EXPIRED&adTypeFilterId=REALESTATE_LETTING`}
                            onClick={() => {
                                trackGoToExpiredAds();
                            }}>
                            Gå videre her <span className="sr-only">for å republisere en eksisterende FINN-annonse</span>
                        </a>
                    </div>
                </div>
            </section>

            <section
                className="mx-auto px-[16] justify-self-center my-[32] md:px-[128] flex flex-col md:flex-row gap-8 md:gap-[80]"
                style={{ maxWidth: '1392px' }}>
                <div className="flex gap-8">
                    <img
                        src="/realestate/lettings/icons/qasa/icon-garantert-innbetaling-av-husleie.svg"
                        height="24px"
                        width="24px"
                        alt="Ikon for garantert innbetaling av husleie"
                    />
                    <div className="t4 mt-4">Garantert innbetaling av husleie</div>
                </div>
                <div className="flex gap-8">
                    <img
                        src="/realestate/lettings/icons/qasa/icon-forsikret-gjennom-qasa.svg"
                        height="24px"
                        width="24px"
                        alt="Ikon for forsikret gjennom Qasa"
                    />
                    <div className="t4 mt-4">Forsikret gjennom Qasa</div>
                </div>
                <div className="flex gap-8">
                    <img
                        src="/realestate/lettings/icons/qasa/icon-beskyttelse-og-bistand.svg"
                        height="24px"
                        width="24px"
                        alt="Ikon for beskyttelse og bistand"
                    />
                    <div className="t4 mt-4">Beskyttelse og bistand</div>
                </div>
                <div className="flex gap-8">
                    <img
                        src="/realestate/lettings/icons/qasa/icon-enklere-for-leietakere.svg"
                        height="24px"
                        width="24px"
                        alt="Ikon for enklere for leietakere"
                    />
                    <div className="t4 mt-4">Enklere for leietakere</div>
                </div>
            </section>
            <QasaSection className="pb-[64]">
                <p className="text-center t1 mb-24 ">Som utleier får du alltid</p>
                <p className="text-center mb-[48]">
                    Følgene tjenester følger med ved signering av Qasa leiekontrakt, og koster 2,49 % av månedlig husleie. Beløpet trekkes
                    fra månedlig husleie.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-12 mb-16">
                    <QasaInfoBox
                        heading="Leiegaranti"
                        content="Få husleien din på forfall, selv om leietakers betaling uteblir eller er forsinket. Qasa sørger for at du får leien på konto i opptil 6 måneder."
                    />
                    <QasaInfoBox
                        heading="Utleieforsikring"
                        content="Qasa sørger for at du som utleier alltid er trygg gjennom en utleieforsikring. Utleieforsikringen er alltid inkludert når du leier ut via en Qasa-leiekontrakt."
                    />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-12">
                    <QasaInfoBox
                        heading="Slipp stress med depositum"
                        content="Qasa garanterer alltid depositumet for deg som utleier. Enten via depositumskonto eller depositumsgaranti for leietakeren, så slipper du å bekymre deg for det."
                    />
                    <QasaInfoBox
                        heading="Digital kontrakt med BankID signering"
                        content="Signer leiekontrakten som den er, eller tilpass den etter dine behov. Vår kontrakt er utarbeidet av jurister og følger markedsstandarder for å være trygg for både deg og leietakeren."
                    />
                    <QasaInfoBox
                        heading="Automatisk fakturering av husleien"
                        content="Se tidligere og kommende leieinnbetalinger, rapporter hendelser og administrer leieforholdet ditt – alt på ett sted."
                    />
                </div>
            </QasaSection>

            <QasaSection className="pb-[64]">
                <p className="text-center t1 mb-24">Som leietaker kan du ta det helt med ro</p>
                <p className="text-center mb-[48]">
                    Følgene tjenester følger med ved signering av Qasa leiekontrakt og koster 2,49 % av månedlig husleie. Beløpet legges til
                    månedlig husleie.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
                    <QasaInfoBox
                        heading="Depositumsfritt"
                        content="Spar pengene dine til noe annet – vi tar oss av depositumet. Med Qasa leier du uten depositum som standard, dersom du har en gyldig kredittsjekk*, i stedet for 3 måneders depositum. "
                    />
                    <QasaInfoBox
                        heading="Trygg betalingsløsning"
                        content="Når du signerer leiekontrakten gjennom Qasa, håndterer vi alle betalinger på en trygg måte."
                    />
                    <QasaInfoBox
                        heading="Flytt inn først, betal senere"
                        content="Forsikre deg om at alt er som avtalt før du betaler første husleie."
                    />
                    <QasaInfoBox
                        heading="Verifisert utleier med digital kontrakt signert med Bank ID"
                        content="Vår leiekontrakt er utarbeidet av våre jurister og følger alle markedsstandarder for å sikre dine rettigheter. Utleier er verifisert med Bank ID, og kontrakten mellom dere signeres med Bank ID."
                    />
                </div>
                <p className="mt-16 text-s text-center">
                    *For å være kvalifisert for depositumsfri leie, må leietaker ha en godkjent kredittsjekk og andre krav.
                </p>
                <div className="flex justify-center mt-32 mb-64">
                    <div className="w-[500px]">
                        <Button
                            secondary
                            fullWidth
                            href={`${baseUrl}/realestate/lettings/search.html?filters=&q=qasa`}
                            className="flex items-center gap-8">
                            Se Qasa-annonser på FINN
                            <IconLinkExternal16 aria-hidden />
                        </Button>
                    </div>
                </div>

                <p className="text-center t1 mb-16 md:mt-48 ">Finn ditt neste hjem enklere med Qasa Premium</p>
                <p className="text-center mb-[32]">
                    Som leietaker kan du velge å bruke Premium-funksjoner for å øke sjansene dine for å finne din neste bolig.
                </p>
                <div className="flex flex-col md:flex-row gap-8 md:gap-[80] justify-self-center">
                    <div className="flex gap-8">
                        <img
                            src="/realestate/lettings/icons/qasa/icon-supersoknad.svg"
                            height="24px"
                            width="24px"
                            alt="Ikon for supersøknad"
                        />
                        <div className="t4 mt-4">Supersøknad</div>
                    </div>
                    <div className="flex gap-8">
                        <img
                            src="/realestate/lettings/icons/qasa/icon-eksklusive-innsikter.svg"
                            height="24px"
                            width="24px"
                            alt="Ikon for eksklusive innsikter"
                        />
                        <div className="t4 mt-4">Eksklusive innsikter</div>
                    </div>
                    <div className="flex gap-8">
                        <img
                            src="/realestate/lettings/icons/qasa/icon-premiumprofil.svg"
                            height="24px"
                            width="24px"
                            alt="Ikon for premium profil"
                        />
                        <div className="t4 mt-4">Premiumprofil</div>
                    </div>
                </div>
            </QasaSection>

            <section className="mx-auto md:p-8 p-16" style={{ maxWidth: '1024px' }}>
                <div className="flex flex-col items-center mt-16">
                    <h2 className="mb-32 md:mt-48 text-center md:text-left">Spørsmål og svar på ting du kanskje lurer på</h2>
                    <div className="w-full sm:w-auto max-w-[500px]">
                        <h3 className="t3 mb-24">For utleier</h3>
                        <ExpandableQuestion
                            question="Hva er Qasa?"
                            answer={[
                                'Qasa er en ledende skandinavisk plattform som forenkler langtidsleie av boliger ved å tilby sikre, smarte og brukervennlige tjenester for både utleiere og leietakere. Tenk på det som Airbnb for langtidsleie – en fullverdig løsning som sikrer tillit, åpenhet og bekvemmelighet gjennom hele leieprosessen.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Hvor mye koster Qasa for meg som utleier?"
                            answer={[
                                unleash?.qasaFreeAdFeeEnabled
                                    ? 'Utleieren betaler en serviceavgift på 2,49 % av den månedlige leien. Denne betalingen trekkes fra den månedlige husleien og håndteres automatisk av Qasa. For annonsering på både Qasa og FINN betaler utleieren 0 kr.'
                                    : 'Utleieren betaler en serviceavgift på 2,49 % av den månedlige leien. Denne betalingen trekkes fra den månedlige husleien og håndteres automatisk av Qasa. For annonsering på både Qasa og FINN betaler utleieren 699 kr, og dette vil bli tilbakebetalt når en leieavtale inngås.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Hva er forskjellen på FINN og Qasa?"
                            answer={[
                                'FINN er en markedsplass som viser alle objekter som er tilgjengelig for langtidsleie i det norske markedet, både fra private og profesjonelle utleiere. Som privatutleier har du muligheten til via FINN å publisere en rubrikkannonse for utleie, og håndtere resten av prosessen selv. Qasa er en ledende skandinavisk plattform som forenkler langtidsleie av boliger ved å tilby sikre, smarte og brukervennlige tjenester for både utleiere og leietakere.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Er Qasa et forsikringsselskap eller står et annet selskap bak forsikringen?"
                            answer={[
                                'Qasa er en ledende skandinavisk plattform som forenkler langtidsleie av boligen. Det er vår partner Tryg som står bak utleieforsikringen som er en del av løsningen.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Hvordan fungerer forsikringen, og hva er vilkårene?"
                            answer={[
                                'Utleieforsikringen er en standard forsikring i Norge. Den dekker ubetalt leie, rimelige saksomkostninger knyttet til inndrivelse av utestående leie, samt skader og mangler på leieobjektet. Forsikringen dekker også mangler i henhold til husleieloven. Maksimal dekning per skade er 250 000 NOK, med en egenandel tilsvarende tre måneders husleie.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Hva koster det for leietaker?"
                            answer={[
                                'Leietaker betaler en serviceavgift på 2,49 % av den månedlige leien. Denne betalingen håndteres automatisk av Qasa.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Hvordan fungerer depositum/depositumsgaranti?"
                            answer={[
                                'Qasa vil foreta en kredittvurdering av alle leietakere. De som blir godkjent, vil få tilbud om en depositumsgaranti, mens de som ikke blir godkjent, vil få tilbud om en depositumskonto. Uansett hvilket alternativ man får tilbudt, er det inkludert i prisen.',
                                'Med en depositumsgaranti trenger leietaker ikke å betale inn et depositum, men kan flytte inn uten denne kostnaden. I stedet stiller Qasa en garanti som dekker opptil tre måneders depositum dersom det skulle bli nødvendig, akkurat som om pengene var innbetalt. Garantien er en del av Qasa-produktet og er inkludert i prisen.',
                                'Dersom leietaker får en depositumskonto, opprettes denne hos Qasas samarbeidspartner. Kontoen er en ordinær depositumskonto i henhold til husleieloven, og det påløper renter. Det er ingen kostnad for å opprette depositumskontoen – dette er inkludert i prisen for å bruke Qasa.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Hva om jeg vil bruke min egen kontrakt?"
                            answer={[
                                'Qasa har sammen med sine partnere en trygg og god kontrakt som brukes i løsningen. Denne kontrakten vil fylles ut dynamisk gjennom prosessen, og du vil som utleier kunne se den i prosessen med leietaker. Etter at kontrakten er signert av begge parter vil den sendes deg på mail, samt være tilgjengelig i ditt dashboard for nedlasting. Det er dessverre ikke mulig å bruke noen annen kontrakt.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Hvordan garanterer Qasa at jeg får leie på konto hver måned?"
                            answer={[
                                'Qasa garanterer leieinntekten. Hvis en leietaker ikke betaler, får utleieren likevel betalt – til rett tid, hver måned. Dette inngår i den månedlige kostnaden for bruk av løsningen.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Publiseres annonsen både på FINN og Qasa?"
                            answer={[
                                'Ja, annonsen din vil være synlig både hos FINN og Qasa. Alle stegene i selve utleieprosessen og dialogen med leietaker vil skje på Qasa sin plattform.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Hvor lenge vil annonsen min være publisert?"
                            answer={[
                                'Annonsen din vil være synlig i minst 49 dager.',
                                'Så lenge du er aktiv på profilsiden din hos Qasa, vil annonsen fortsette å være publisert helt til boligen blir leid ut – eller til du selv velger å sette den som inaktiv.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Hvor kan jeg endre og redigere annonsen min?"
                            answer={[
                                'Du kan enkelt redigere og administrere annonsen din på Qasa. Alle endringer du gjør der, blir automatisk oppdatert og synlige både på Qasa og på FINN.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Hvordan får leietakere kontakt med meg?"
                            answer={[
                                'Annonsen din er synlig både på Qasa og på FINN.',
                                'All kommunikasjon med potensielle leietakere skjer via chat på Qasa – der annonsen din har en direkte lenke slik at interessenter enkelt kan ta kontakt med deg.',
                            ]}
                        />
                    </div>
                    <div className="w-full sm:w-auto max-w-[500px]">
                        <h3 className="t3 mt-32 mb-24">For leietaker</h3>
                        <ExpandableQuestion
                            question="Er Qasa depositumsfritt?"
                            answer={[
                                'Qasa tilbyr utleiere en leiegaranti, slik at leietakere ikke trenger å betale et tradisjonelt depositum etter at en kredittsjekk er gjennomført. Det betyr flere tilgjengelige boliger, spesielt for unge, nyankomne og familier.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Koster det noe for meg som leietaker å bruke Qasa?"
                            answer={[
                                'Leietaker betaler en serviceavgift på 2,49 % av den månedlige leien. Denne betalingen legges på den månedlige husleien og håndteres automatisk av Qasa.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Hva er forskjellen på FINN og Qasa?"
                            answer={[
                                'FINN er en markedsplass som viser alle objekter som er tilgjengelig for langtidsleie i det norske markedet, både fra private og profesjonelle utleiere. Som privatutleier har du muligheten til via FINN å publisere en rubrikkannonse for utleie, og håndtere resten av prosessen selv. Qasa er en ledende skandinavisk plattform som forenkler langtidsleie av boliger ved å tilby sikre, smarte og brukervennlige tjenester for både utleiere og leietakere.',
                            ]}
                        />
                        <ExpandableQuestion
                            question="Hva skjer om jeg ikke har gyldig kredittsjekk?"
                            answer={[
                                'Er det slik at du ikke har en gyldig kredittsjekk, så vil du kunne betale inn 3 mnd depositum og fortsatt leie via Qasa.',
                            ]}
                        />
                        <Expandable
                            style={{ backgroundColor: '#FFF3F7', padding: '16px', borderRadius: '8px', marginTop: '4px' }}
                            onChange={setExpanded}
                            expanded={expanded}
                            buttonClass="w-full"
                            title={<span className="mb-0 font-bold text-s py-8">Kan jeg leie en Qasabolig om jeg har NAV-garanti?</span>}>
                            <div>
                                I løsningen til Qasa kan ikke NAV-garanti benyttes. Løsningen er depositumfri hvis man består kredittsjekk,
                                ellers har man mulighet til å betale depositum.
                            </div>
                        </Expandable>
                    </div>
                </div>
            </section>

            <section className="mx-auto md:p-8 p-16" style={{ maxWidth: '1024px' }}>
                <div className="flex flex-col items-center">
                    <h2 className="mb-16 mt-16 text-center md:text-left">Kom i gang med en trygg leie</h2>
                    <p className="max-w-[500px] text-center md:text-left">
                        Med Qasa får du en annonse som vises både på Qasa og FINN, i tillegg til annonsen tilbyr Qasa en rekke tjenester som
                        bistår og hjelper deg som utleier til å få et tryggere og mer forutsigbart utleieforhold.
                    </p>
                </div>
                <div className="flex justify-center mb-64 mt-24">
                    <div className="w-[500px]">
                        <Button
                            href="/travel-api/lotr/qasa/create-listing-landing"
                            secondary
                            fullWidth
                            onClick={() => {
                                trackCreateOrRepublishAdButton('qasa', null, false);
                            }}>
                            <div className="flex items-center justify-center gap-8">
                                <IconLinkExternal16 aria-hidden />
                                Publiser og lei ut med Qasa
                            </div>
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AboutQasaPage;
